import React, { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { UserContext } from '../../../../../context/UserContext';
import { InviteUsersResponse, InviteUsersVariables, UserMutations } from '../../../../../graphql/user.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import {
  CompanyRole,
  CompanyRoleAssignment,
  CompanyRoleAssignmentInput,
  CompanyRoleAssignmentUtil,
} from '../../../../../types/company_role_assignment';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Button, CancelButton } from '../../../../components/Button';
import { DropdownSelect } from '../../../../components/Dropdown';
import { ActionModal } from '../../../../components/Modal';
import MultiEmailInput from '../../../../components/MultiTextInput/templates/MultiEmailInput';
import { ToastType } from '../../../../components/Toast';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../types/custom_field';
import CustomFieldInput from '../../../Common/CustomFieldInput';
import { ContactContext } from '../../../../../context/ContactContext';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';

interface InviteUserModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function InviteUserModal({ open, setOpen }: InviteUserModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { companyRoles, setCompanyRoles } = useContext(CompanyRoleAssignmentContext);
  const { refreshCompanyUsers, companyUsers } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { contacts } = useContext(ContactContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const companyId = currentCompany.id;

  const [emails, setEmails] = useState(new Set<string>());
  const [input, setInput] = useState<CompanyRoleAssignmentInput>(
    new CompanyRoleAssignmentInput({ companyId, role: CompanyRole.employee, email: 'invite@ventory.io' }),
  );

  const [inviteUsers, { loading }] = useMutation<InviteUsersResponse, InviteUsersVariables>(UserMutations.invite, {
    onCompleted: response => {
      response.inviteUsers.forEach(cra => {
        const item = new CompanyRoleAssignment(cra);

        const existing = companyRoles.get(cra.userId);
        if (existing) companyRoles.set(cra.userId, [...existing, item]);
        else companyRoles.set(cra.userId, [item]);
      });
      setCompanyRoles(new Map(companyRoles));
      refreshCompanyUsers(companyId);
      showToast({
        type: ToastType.success,
        title: t(TranslationKey.usersInvitedSuccessfullyToastTitle).singular.upper,
      });
      setOpen(false);
    },
    variables: { companyId, role: input.forCreate(), emails: [...emails] },
  });

  const relevantCustomFields = useMemo(() => {
    return [...customFields.values()]
      .filter(cf => cf.entityType === CustomFieldEntityType.companyRole && !cf.hidden && !cf.generated)
      .sort((a, b) => (a.mandatory ? -1 : a.index - b.index || a.name.localeCompare(b.name)));
  }, [customFields]);

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        text={t(TranslationKey.invite).singular.upper}
        onClick={() => inviteUsers()}
        disabled={!emails.size}
        loading={loading}
      />
    </div>
  );

  return (
    <ActionModal
      open={open}
      setOpen={setOpen}
      title={t(TranslationKey.inviteUser).plural.upper}
      footer={footer}
      width={'500px'}
    >
      <div className='grid gap-4'>
        <MultiEmailInput emails={emails} setEmails={setEmails} label={t(TranslationKey.email).plural.upper} />
        <DropdownSelect
          label={t(TranslationKey.role).singular.upper}
          items={Object.values(CompanyRole)}
          onSelect={role => setInput(input.withRole(role))}
          textBuilder={CompanyRoleAssignmentUtil.roleToString}
          value={input.role}
        />
        {!!relevantCustomFields.length && (
          <div className='grid grid-cols-2 gap-2'>
            {relevantCustomFields.map(cf => (
              <div className='w-full'>
                <CustomFieldInput
                  item={cf}
                  value={input.customFields.get(cf.id)?.value}
                  entity={input}
                  onChange={setInput}
                  users={companyUsers}
                  contacts={contacts}
                  productMasterData={productMasterData}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </ActionModal>
  );
}
